// Libs
import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign, faAppleAlt, faCalendarWeek, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { Fragment } from "react";

// Components & Utils
import MealPlanApi from "../api/MealPlans";
import OnceOffMealModal from "../components/OnceOffMealModal";
import EmergencyOrderModal from "../components/EmergencyOrderModal";
import CancelMealModal from "../components/CancelMealModal";
import MealsToAddForDateButton from "../components/MealsToAddForDateButton";
import { useGlobalValue } from "../utils/GlobalState";
import { setIsLoading, setErrors } from "../utils/Actions";
import { formatMoney } from "../utils/Helpers";
import MealsForDate from "../components/MealsForDate";
import ClientApi from "../api/Client";
import { getDays } from "../utils/DateHelper";
import { getCutOffDate } from "../services/mealplans";

export default function MealPlans() {
  const [{ sysCfg, refresh, user }, dispatch] = useGlobalValue();
  const [meals, setMeals] = useState([]);
  const [date, setDate] = useState(moment());
  const [mealToCancel, setMealToCancel] = useState();
  const [cancelMealModal, setCancelMealModal] = useState(false);
  const [menuModal, setMenuModal] = useState({ show: false, date: moment() });
  const [emergencyModel, setEmergencyModel] = useState({ show: false, date: moment() });
  const [weeklyTotalQuantity, setWeeklyTotalQuantity] = useState(0);
  const [weeklyTotalCost, setWeeklyTotalCost] = useState(0);
  const [todayTotalQuantity, setTodayTotalQuantity] = useState(0);
  const [hiddenDays, setHiddenDays] = useState([]);
  const [cutOffDate, setCutOffDate] = useState(moment());

  // Dates
  const today = useMemo(() => {
    return moment();
  }, []);

  // On Mount
  useEffect(() => {
    setIsLoading(true, dispatch);

    const start = date.clone().startOf("week");
    const end = date.clone().endOf("week");
    let weekQuantity = 0;
    let weekCost = 0;
    let todaysQuantity = 0;

    // By default, the week starts on Sunday
    // But we want it to start on Monday
    start.add(1, "days");
    end.add(1, "days");

    MealPlanApi.getMeals(start, end)
      .then((meals) => {
        // Create a key for every day of the week
        let date = start.clone();
        while (date.isBefore(end)) {
          let dateKey = date.format("YYYY-MM-DD");

          if (!meals.hasOwnProperty(dateKey)) {
            meals[dateKey] = [];
          }

          date.add(1, "day");
        }

        // Sort by date
        const orderedByDate = {};
        Object.keys(meals)
          .sort()
          .forEach(function (key) {
            orderedByDate[key] = meals[key];
            let formattedDate = moment(key, 'YYYY-MM-DD');

            //confirm date is not to be hidden
            if (sysCfg.mealordering_days_hide.indexOf(formattedDate.format("d")) === -1) {
              meals[key].forEach(meal => {
                //setting todays quantity
                if (key === today.format('YYYY-MM-DD')) {
                  todaysQuantity += meal.qty;
                }

                //updating weekly total quantity
                weekQuantity += meal.qty;

                //updating weekly total cost
                weekCost += (meal.price * meal.qty);
              });
            }

            //updating states
            setTodayTotalQuantity(todaysQuantity);
            setWeeklyTotalCost(weekCost);
            setWeeklyTotalQuantity(weekQuantity);
          });

        setMeals(orderedByDate);
        setIsLoading(false, dispatch);

        dispatch({
          type: "setRefresh",
          refresh: false,
        });
      })
      .catch((apiErrors) => {
        setErrors(apiErrors, dispatch);
        setIsLoading(false, dispatch);
      });
  }, [today, refresh, dispatch, date, sysCfg.mealordering_days_hide]);

  // Set Hidden Days
  useEffect(() => {
    const daysToHide = (sysCfg.mealordering_days_hide.length > 0) ? JSON.parse(sysCfg.mealordering_days_hide) : [];
    if (daysToHide.length > 0) {
      setHiddenDays(daysToHide);
    }

    //update cut off date
    setCutOffDate(getCutOffDate(sysCfg));

    // Lookup client "Delivery Day" custom field
    const fetchClientData = async () => {
      const clientData = await ClientApi.getClient(user.client_id);
      const delDay = Object.values(clientData.fields).filter(i => i.name === "Delivery Day");
      const allDays = getDays();

      if (delDay.length > 0) {
        const excludedDays = Object.keys(allDays).filter(day => allDays[day] !== delDay[0].value).map(Number);
        setHiddenDays(excludedDays);
      }
    }
    fetchClientData();
  }, [sysCfg, user]);

  const goBack = () => {
    let tmp = date.clone();
    tmp.subtract(1, "week");
    setDate(tmp);
  };

  const goForward = () => {
    let tmp = date.clone();
    tmp.add(1, "week");
    setDate(tmp);
  };

  const setDateToToday = () => {
    setDate(moment());
  }

  const cancelMeal = (meal) => {
    setMealToCancel(meal);
    setCancelMealModal(true);
  };

  return (
    <div>
      <div className="row">
        <div className="col-sm-12 text-right mb-3">
          {sysCfg.mealordering_recurring_mealplans === 1 ? (
              <div style={{display: "inline-block", paddingLeft: "20px"}}>
                <Link to="/recmealplans" className="btn btn-outline-warning">
                  View Recurring Meal Plans
                </Link>
              </div>
          ) : (
              ''
          )}
          {sysCfg.mealordering_emergency_order === 1 ? (
              <div style={{display: "inline-block", paddingLeft: "20px"}}>
                <button
                    className="btn btn-warning"
                    onClick={() => {
                      setEmergencyModel({show: true, date: date});
                    }}
                >
                  Emergency Order
                </button>
              </div>
          ) : (
              ''
          )}
        </div>
      </div>
      {/* Indicators Row */}
      <div className="row">
        <div className="col-lg-4 col-sm-12 mb-3">
          <div className="mini-stats-wid card">
            <div className="card-body">
              <div className="d-flex">
                <div className="flex-grow-1">
                  <p className="text-muted fw-medium">Number of Meals for Today</p>
                  <h4 className="mb-0">{todayTotalQuantity}</h4>
                </div>
                <div className="avatar-sm rounded-circle bg-danger align-self-center mini-stat-icon">
                  <span className="avatar-title rounded-circle bg-danger">
                    <FontAwesomeIcon icon={faAppleAlt} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-sm-12 mb-3">
          <div className="mini-stats-wid card">
            <div className="card-body">
              <div className="d-flex">
                <div className="flex-grow-1">
                  <p className="text-muted fw-medium">Number of Meals for the Week</p>
                  <h4 className="mb-0">{weeklyTotalQuantity}</h4>
                </div>
                <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                  <span className="avatar-title rounded-circle bg-primary">
                    <FontAwesomeIcon icon={faCalendarWeek} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-4 col-sm-12 mb-3">
          <div className="mini-stats-wid card">
            <div className="card-body">
              <div className="d-flex">
                <div className="flex-grow-1">
                  <p className="text-muted fw-medium">Total Week Cost</p>
                  <h4 className="mb-0">{formatMoney(weeklyTotalCost)}</h4>
                </div>
                <div className="avatar-sm rounded-circle bg-success align-self-center mini-stat-icon">
                  <span className="avatar-title rounded-circle bg-success">
                    <FontAwesomeIcon icon={faDollarSign} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-3 mb-3">
        <div className="col-4 text-left">
          <button className="btn btn-primary btn-sm" onClick={goBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button className="btn btn-primary btn-sm mx-1" onClick={setDateToToday}>Today</button>
          <button className="btn btn-primary btn-sm" onClick={goForward}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
      <div className="row">
        {Object.keys(meals).map((date) => {
          let mealsOnDay = meals[date];
          let formattedDate = moment(date, "YYYY-MM-DD");
          const disabled = formattedDate.isBefore(cutOffDate);

          return (
            <Fragment key={date}>
              {(hiddenDays.indexOf(+formattedDate.format("d")) === -1) | (mealsOnDay.length > 0) ? (
                <div className="col-sm-4">
                  <MealsToAddForDateButton hiddenDays={hiddenDays} formattedDate={formattedDate} disabled={disabled} setMenuModal={setMenuModal} />
                  <h3 style={{ display: "inline", marginLeft: 10 }}>{formattedDate.format("dddd Do MMM")}</h3>
                  <br />
                  <br />
                  <table className="table border-0 table-small">
                    <tbody>{(mealsOnDay.length > 0) ? <MealsForDate mealsOnDay={mealsOnDay} today={today} formattedDate={formattedDate} cancelMeal={cancelMeal} /> : (
                      <tr>
                        <td className="text-center" style={{ color: "black" }}>
                          No Meals Selected To Be Delivered
                        </td>
                      </tr>
                    )}
                      <tr className="bg-light-grey border-bottom"><td className="text-white border-0 rounded-left"><b>Qty:</b></td><td className="text-right text-white border-0 rounded-right" colSpan={2}><b>{mealsOnDay.reduce((sum, meal) => sum + ((!meal.cancelled && !meal.suspended) ? meal.qty : 0), 0)}</b></td></tr>
                      <tr className="bg-light-grey">
                        <td className="text-white border-0 rounded-left">
                          <b>Total:</b>
                        </td>
                        <td className="text-right text-white border-0 rounded-right" colSpan={2}>
                          <b>{formatMoney(mealsOnDay.reduce((sum, meal) => sum + ((!meal.cancelled && !meal.suspended) ? (meal.price * meal.qty) : 0), 0))}</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <></>
              )}
            </Fragment>
          );
        })}
      </div>

      <EmergencyOrderModal
        show={emergencyModel.show}
        date={emergencyModel.date}
        hide={(onceOffDate) => {
          setEmergencyModel({ show: false, date: moment() });
          setMenuModal({ show: true, date: onceOffDate });
        }}
      />

      <OnceOffMealModal
        show={menuModal.show}
        date={menuModal.date}
        hide={() => setMenuModal({ show: false, date: moment() })}
      />

      <CancelMealModal show={cancelMealModal} hide={() => setCancelMealModal(false)} meal={mealToCancel} />
    </div>
  );
}
